import { useUpdateUserMutation } from '@axilon-apps/domains-identity/state'
import { useFeedbackMessage, useFlashMessages } from '@axilon-apps/libs-ui-components'
import type {
  EntitiesUpdateCurrentUserRequest,
  FieldErrors
} from '@axilon-apps/domains-identity/types'

import { UsersFlashMessageGroup } from '../../../constants/users-flash-message-groups'

interface ErrorResponse {
  message?: string
  fields?: Record<string, string[]>
}

interface Props {
  userId: string
  onSuccess?: () => void
}

const useRenameUser = ({ userId, onSuccess }: Props) => {
  const { toast } = useFeedbackMessage()
  const flash = useFlashMessages(UsersFlashMessageGroup.RenameUser)
  const [updateUserMutation, result] = useUpdateUserMutation()

  const fieldErrors = (result.error as ErrorResponse)?.fields as
    | FieldErrors<EntitiesUpdateCurrentUserRequest>
    | undefined

  const submit = async ({ name, username, email }: EntitiesUpdateCurrentUserRequest) => {
    flash.clear()
    const trimmedEmail = email?.trim()
    const { data, error } = await updateUserMutation({
      id: userId,
      entitiesUpdateCurrentUserRequest: {
        name,
        username,
        ...(trimmedEmail ? { email: trimmedEmail } : {})
      }
    })

    if (data) {
      toast.success({ message: `User ${data.username} was updated successfully` })
      onSuccess?.()
    }

    if (error) {
      flash.error({ message: error.message })
    }
  }

  const reset = () => {
    result.reset()
    flash.clear()
  }

  return {
    submit,
    fieldErrors,
    reset,
    result
  }
}

export default useRenameUser
